import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { useLocalStorage } from '@vueuse/core'

export const usePwaStore = defineStore('pwa', () => {
  // === Installability ===
  const canInstall = ref(false)
  const installPromptDeferred = shallowRef<BeforeInstallPromptEvent | null>(null)
  const isStandalone = ref(false)
  const installPromptDismissedUntil = useLocalStorage<number | null>('lavmag.pwa.installDismissedUntil', null)

  // === Sterowanie widocznością modala instalacji ===
  // Widoczność w store (nie lokalnie w komponencie), by stały przycisk mógł otworzyć
  // modal także w trakcie cooldownu (ręczne otwarcie pomija shouldShowInstallPrompt).
  const installModalOpen = ref(false)
  const installModalSource = ref<'auto' | 'manual' | 'post-purchase'>('auto')

  // === Push notifications ===
  const pushPermission = ref<'default' | 'granted' | 'denied' | 'unavailable'>('default')
  const pushSubscriptionEndpoint = useLocalStorage<string | null>('lavmag.pwa.pushSubEndpoint', null)
  const pushSoftAskDismissedUntil = useLocalStorage<number | null>('lavmag.pwa.softAskDismissedUntil', null)

  // === SW lifecycle ===
  const updateAvailable = ref(false)
  const swRegistration = shallowRef<ServiceWorkerRegistration | null>(null)

  // === Engagement tracking ===
  const visitCount = useLocalStorage<number>('lavmag.pwa.visitCount', 0)
  const firstVisitAt = useLocalStorage<number | null>('lavmag.pwa.firstVisitAt', null)
  const lastVisitAt = useLocalStorage<number | null>('lavmag.pwa.lastVisitAt', null)

  function setInstallPromptDismissed(daysCooldown = 30): void {
    installPromptDismissedUntil.value = Date.now() + daysCooldown * 24 * 60 * 60 * 1000
  }

  function setPushSoftAskDismissed(daysCooldown = 14): void {
    pushSoftAskDismissedUntil.value = Date.now() + daysCooldown * 24 * 60 * 60 * 1000
  }

  function openInstallModal(source: 'auto' | 'manual' | 'post-purchase' = 'auto'): void {
    installModalSource.value = source
    installModalOpen.value = true
  }

  function closeInstallModal(): void {
    installModalOpen.value = false
  }

  /** Test-only helper — increments visitCount once */
  function trackVisitMock(): void {
    visitCount.value++
    if (!firstVisitAt.value) firstVisitAt.value = Date.now()
    lastVisitAt.value = Date.now()
  }

  return {
    canInstall,
    installPromptDeferred,
    isStandalone,
    installPromptDismissedUntil,
    installModalOpen,
    installModalSource,
    pushPermission,
    pushSubscriptionEndpoint,
    pushSoftAskDismissedUntil,
    updateAvailable,
    swRegistration,
    visitCount,
    firstVisitAt,
    lastVisitAt,
    setInstallPromptDismissed,
    setPushSoftAskDismissed,
    openInstallModal,
    closeInstallModal,
    trackVisitMock,
  }
})

declare global {
  interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[]
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed', platform: string }>
    prompt: () => Promise<void>
  }
}
